/**
 * O seletor de modelo de gráfico: botão com o nome do modelo e um painel de cartões, cada um com
 * título e a frase do que ele mostra.
 *
 * É o seletor da Geral, agora compartilhado. Uma faixa de botões (`Segmented`) servia enquanto eram
 * três modelos de nome curto; com oito, ela estoura a largura do cabeçalho e, pior, obriga a
 * escolher por um rótulo de duas palavras — "Mapa de calor" não diz o que desenha. O cartão carrega
 * a explicação junto, que é o que faz escolher sem errar.
 */
import { useRef, useState } from 'react';
import { ChartSpline, ChevronDown } from 'lucide-react';
import { AnchoredPopover, GhostBtn } from '@/components/v2/ui';
import { v } from '@/components/v2/theme';

/** Um modelo: a chave que fica salva, o nome do botão e a frase do cartão. */
export interface PickModel { k: string; l: string; d: string }

export function ModelPicker({ opts, value, onChange }: {
    opts: PickModel[];
    value: string;
    onChange: (k: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const trigger = useRef<HTMLDivElement>(null);
    const now = opts.find((o) => o.k === value) ?? opts[0];
    if (!now) return null;

    return (
        <div ref={trigger} style={{ position: 'relative' }}>
            <GhostBtn onClick={() => setOpen((x) => !x)} active={open}>
                <ChartSpline size={14} color={v('muted')} />
                {now.l}
                <ChevronDown size={13} color={v('faint')} />
            </GhostBtn>
            <AnchoredPopover open={open} onClose={() => setOpen(false)} width={520} trigger={trigger}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
                {opts.map((o) => {
                    const on = o.k === now.k;
                    return (
                        <button key={o.k} type="button" className={on ? '' : 'rv-h-hover'}
                            onClick={() => { onChange(o.k); setOpen(false); }}
                            style={{
                                textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 3,
                                padding: '10px 12px', borderRadius: 10, cursor: 'pointer', font: 'inherit',
                                border: `1px solid ${on ? v('text') : v('border-2')}`,
                                background: on ? v('border') : 'transparent', color: 'inherit',
                            }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{o.l}</span>
                            <span style={{ fontSize: 11.5, color: v('muted'), lineHeight: 1.4 }}>{o.d}</span>
                        </button>
                    );
                })}
            </AnchoredPopover>
        </div>
    );
}
